import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { IconName } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { Box, Breakpoint, Container, ContainerRow, Grid, Link, Navigation, Stack, TextAlpha, TopNavigationLayout, useMediaQueryMatchesOnDesktop, Image, } from '@yonderland/andes-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountMenuItem from '../components/header/AccountMenuItem';
import BasketMenuItem from '../components/header/BasketMenuItem';
import LanguageMenuItem from '../components/header/LanguageMenuItem';
import MenuSearchContainer from '../components/header/MenuSearchContainer';
import MobileNavigationContainer from '../components/header/MobileNavigationContainer';
import QuickLinksItem from '../components/header/QuickLinksItem';
import WishListMenuItem from '../components/header/WishlistMenuItem';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { getTeaserCellColumns, getTeaserCellRows, getTeaserColumns } from '../util/NavigationUtil';
import { generateTestId } from '../util/TestUtil';
import TeaserContainer from './TeaserContainer';
const TEASER_COLUMN_KEY = 'teaserColumn';
const TEST_ID = 'flyout';
const HeaderContainer = ({ topNavigation, mainNavigation, queryValue }) => {
    var _a, _b;
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { pageUrls, root, lang, alternateUrl, isShopLanding, isTransactionalWebShop } = useAemConfig();
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const [selectedTab, setSelectedTab] = useState(null);
    const [content, setContent] = useState(null);
    const [hiddenItems, setHiddenItems] = useState([]);
    const [teaserType, setTeaserType] = useState('');
    const isLanguageMenuVisible = alternateUrl && Object.keys(alternateUrl).length > 1;
    const getSearchContent = () => {
        if (pageUrls.categoriesParentPage && pageUrls.brandsParentPage && isTransactionalWebShop) {
            return (_jsx(Box, { zIndex: !isDesktop ? 500 : undefined, children: _jsx(MenuSearchContainer, { suggestionsParentLink: root, brandsParentLink: pageUrls.brandsParentPage, categoriesParentLink: pageUrls.categoriesParentPage, queryValue: queryValue }) }));
        }
        return null;
    };
    const getMainLevels = () => {
        var _a;
        const flyoutsMap = mainNavigation.desktop.flyouts;
        const orderedKeys = Object.keys(flyoutsMap);
        const orderedFlyouts = orderedKeys.map(key => flyoutsMap[key]).filter(f => !f.hideMenu);
        let finalList = orderedFlyouts.map(item => {
            if ((item === null || item === void 0 ? void 0 : item.spaceType) === 'simple') {
                return Object.assign(Object.assign({}, item), { type: 'divider', label: '|' });
            }
            return item;
        });
        if ((_a = mainNavigation.nonCommercialLinks) === null || _a === void 0 ? void 0 : _a.length) {
            const separator = {
                id: 'separator',
                type: 'separator',
                label: 'separator',
            };
            finalList = [...finalList, separator, ...mainNavigation.nonCommercialLinks];
        }
        return finalList.map(elem => {
            var _a;
            return ({
                id: elem.id,
                label: elem.label,
                type: elem.type,
                path: (_a = elem === null || elem === void 0 ? void 0 : elem.path) !== null && _a !== void 0 ? _a : '',
            });
        });
    };
    const navItems = useMemo(getMainLevels, [mainNavigation]);
    const getFlyoutColumns = (flyoutItem) => {
        var _a, _b, _c, _d, _e;
        const flyout = mainNavigation.desktop.flyouts[flyoutItem.id];
        if (!flyout)
            return [];
        const columnsMap = flyout[':items'];
        const columnsOrder = (_a = flyout[':itemsOrder']) !== null && _a !== void 0 ? _a : [];
        setTeaserType((_b = flyout.teaserColumnLayout) !== null && _b !== void 0 ? _b : '');
        if (!columnsMap || !columnsOrder.length)
            return [];
        const result = [];
        for (const columnKey of columnsOrder) {
            const columnObj = columnsMap[columnKey];
            if (!columnObj)
                continue;
            const columnItems = [];
            let columnSpacerActive = false;
            for (const rootItem of Object.values(columnObj)) {
                if (!rootItem || rootItem.hideMenu)
                    continue;
                const childItems = [];
                const nestedItems = (_c = rootItem[':items']) !== null && _c !== void 0 ? _c : {};
                const nestedOrder = (_d = rootItem[':itemsOrder']) !== null && _d !== void 0 ? _d : [];
                let rootSpacerActive = false;
                for (const itemKey of nestedOrder) {
                    if (itemKey === 'spacer' || ((_e = nestedItems[itemKey]) === null || _e === void 0 ? void 0 : _e.spaceType) === 'simple') {
                        rootSpacerActive = true;
                        continue;
                    }
                    const rawItem = nestedItems[itemKey];
                    if (!rawItem || !('type' in rawItem) || rawItem.hideMenu) {
                        rootSpacerActive = false;
                        continue;
                    }
                    childItems.push(Object.assign(Object.assign({}, rawItem), { afterSpacer: rootSpacerActive }));
                    rootSpacerActive = false;
                }
                columnItems.push(Object.assign(Object.assign({}, rootItem), { afterSpacer: columnSpacerActive, childItems }));
                columnSpacerActive = false;
                if (rootSpacerActive) {
                    columnSpacerActive = true;
                }
            }
            if (columnItems.length) {
                result.push({
                    key: columnKey,
                    items: columnItems,
                });
            }
        }
        return result;
    };
    const buildFlyoutContent = (flyout) => {
        var _a;
        const allColumns = getFlyoutColumns(flyout);
        const teaserColumn = allColumns.find(col => col.key === TEASER_COLUMN_KEY);
        const columns = allColumns.filter(col => col.key !== TEASER_COLUMN_KEY);
        return {
            columns,
            teaserColumn,
            teasers: ((_a = mainNavigation.desktop.flyouts[flyout.id]) === null || _a === void 0 ? void 0 : _a.teasers) || [],
        };
    };
    const handleHiddenItems = useCallback((items) => {
        setHiddenItems(items);
    }, []);
    useEffect(() => {
        getMainLevels();
    }, [mainNavigation]);
    const handleClick = (elem) => {
        if (elem.path) {
            window.location.href = elem.path;
        }
    };
    const mobileMenuConfiguration = useMemo(() => {
        var _a, _b;
        const mobile = structuredClone(mainNavigation.mobile);
        const cleanedItems = {};
        const cleanedItemsOrder = [];
        for (const key of mobile[':itemsOrder']) {
            const item = mobile[':items'][key];
            if (!item || ('spaceType' in item && item.spaceType === 'simple')) {
                continue;
            }
            cleanedItems[key] = item;
            cleanedItemsOrder.push(key);
        }
        mobile[':items'] = cleanedItems;
        mobile[':itemsOrder'] = cleanedItemsOrder;
        const ICONS_MAPPING = {
            stores: IconName.STORE,
            help: IconName.MESSAGE,
            support: IconName.MESSAGE,
        };
        if (((_a = mainNavigation.nonCommercialLinks) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            mobile[':items']['spacer12345'] = {
                type: 'common',
                id: 'spacer-b1318f0345',
                spaceType: 'double',
            };
            mobile[':itemsOrder'].push('spacer12345');
            mainNavigation.nonCommercialLinks.forEach(elem => {
                mobile[':items'][elem.id] = Object.assign(Object.assign({}, elem), { path: elem.path || '', type: elem.type, icon: elem.icon || undefined, testIdSuffix: '-non-commercial' });
                if (!mobile[':itemsOrder'].includes(elem.id.toString())) {
                    mobile[':itemsOrder'].push(elem.id.toString());
                }
            });
        }
        if (((_b = topNavigation.quickLinks) === null || _b === void 0 ? void 0 : _b.length) > 0) {
            mobile[':items']['spacerabc123'] = {
                type: 'common',
                id: 'spacer-b1318f1431',
                spaceType: 'double',
            };
            mobile[':itemsOrder'].push('spacerabc123');
            topNavigation.quickLinks.forEach((elem, index) => {
                mobile[':items'][`quicklink_${index}`] = {
                    ':items': {},
                    ':itemsOrder': [],
                    path: elem.linkPath || '',
                    type: 'common',
                    icon: ICONS_MAPPING[elem.linkKey] || undefined,
                    hideMenu: false,
                    id: `quicklink-${index}`,
                    label: elem.linkName,
                    highlightCategory: false,
                    testIdSuffix: '-quick-link',
                };
                if (!mobile[':itemsOrder'].includes(`quicklink_${index}`)) {
                    mobile[':itemsOrder'].push(`quicklink_${index}`);
                }
            });
        }
        return mobile;
    }, [mainNavigation.mobile, mainNavigation.nonCommercialLinks, topNavigation.quickLinks]);
    return (_jsxs(_Fragment, { children: [_jsx(ContainerRow, { width: "full", children: _jsx(Box, { backgroundColor: "neutral", paddingBlockStart: { base: '3', lg: '4' }, shadow: !isDesktop ? 'menu-initial' : undefined, paddingBlockEnd: { base: '3', lg: '2' }, children: _jsx(Container, { column: "content", children: _jsxs(Stack, { direction: "col", children: [_jsx(Box, { paddingBlockEnd: { base: '2', lg: '0' }, children: _jsxs(TopNavigationLayout, { children: [_jsx(Breakpoint, { until: "lg", children: _jsx(MobileNavigationContainer, { accountConfig: {
                                                        accountMenu: topNavigation.accountMenu || [],
                                                        login: { actionUri: topNavigation.actionUri },
                                                        registration: { link: topNavigation.registrationLink || '' },
                                                        enabled: mainNavigation.mobile.enableAccount,
                                                    }, searchConfig: {
                                                        brandsParentLink: pageUrls.brandsParentPage,
                                                        categoriesParentLink: pageUrls.categoriesParentPage,
                                                        enabled: mainNavigation.mobile.enableSearch,
                                                    }, menuConfiguration: mobileMenuConfiguration }) }), topNavigation.logo && (_jsx(TopNavigationLayout.Logo, { children: _jsx("div", { "data-analytics-bannerid": topNavigation.logo.analyticsBannerId, "data-testid": "home_logo", children: _jsx("a", { href: topNavigation.logo.link, children: _jsx(Image, { height: {
                                                                lg: 60,
                                                                xs: 44,
                                                            }, source: topNavigation.logo.fileReference, alt: "logo alt" }) }) }) })), _jsx(TopNavigationLayout.Search, { children: getSearchContent() }), _jsxs(Stack, { align: "center", gap: "2", justify: "center", children: [_jsx(Breakpoint, { from: "lg", children: _jsxs(Stack, { align: "center", gap: "5", justify: "center", children: [_jsxs(_Fragment, { children: [isLanguageMenuVisible && (_jsx(LanguageMenuItem, { currentLang: lang, defaultLanguages: Object.keys(alternateUrl) })), topNavigation.quickLinks &&
                                                                            topNavigation.quickLinks.map((elem, index) => {
                                                                                return _jsx(QuickLinksItem, Object.assign({}, elem), `${elem.linkKey}-${index}`);
                                                                            })] }), !isShopLanding && isTransactionalWebShop && (_jsx(AccountMenuItem, { isDesktop: true, accountMenu: topNavigation.accountMenu || [], login: { actionUri: topNavigation.actionUri }, registration: { link: topNavigation.registrationLink || '' } }))] }) }), _jsx(Breakpoint, { until: "lg", children: !isShopLanding && isTransactionalWebShop && (_jsx(AccountMenuItem, { isDesktop: false, accountMenu: topNavigation.accountMenu || [], login: { actionUri: topNavigation.actionUri }, registration: { link: topNavigation.registrationLink || '' } })) }), _jsx(Box, { paddingInlineStart: { base: '0', lg: '5' }, display: "flex", children: _jsxs(Stack, { gap: "2", children: [pageUrls.wishlist && !isShopLanding && isTransactionalWebShop && (_jsx(WishListMenuItem, { link: pageUrls.wishlist })), topNavigation.basket && isTransactionalWebShop && (_jsx(BasketMenuItem, Object.assign({ basketOverviewLink: (_a = pageUrls.basketOverview) !== null && _a !== void 0 ? _a : '', checkoutLink: (_b = pageUrls.checkoutPage) !== null && _b !== void 0 ? _b : '' }, topNavigation.basket)))] }) })] })] }) }), _jsx(Breakpoint, { until: "lg", children: _jsx(_Fragment, { children: getSearchContent() }) })] }) }) }) }), _jsx(Breakpoint, { from: "lg", children: mainNavigation.desktop && (_jsx(Box, { backgroundColor: "neutral", position: "relative", shadow: "menu-initial", children: _jsx(Container, { column: "content", children: _jsx(Box, { children: _jsx(Navigation, { testId: "navigation", navItems: navItems, moreCta: t('navigation.more-menu-item'), onItemClick: handleClick, onItemSelect: (elem) => {
                                    if (elem) {
                                        setSelectedTab(elem);
                                        setContent(buildFlyoutContent(elem));
                                    }
                                    else {
                                        setSelectedTab(null);
                                        setContent(null);
                                    }
                                }, onGetHiddenItems: handleHiddenItems, children: _jsx(Box, { position: "absolute", left: "0", right: "0", backgroundColor: "neutral", zIndex: 500, shadow: "menu-initial", children: _jsxs(Container, { column: "content", children: [selectedTab && selectedTab.type === 'common' && content && (_jsx(Navigation.Content, { children: _jsxs(Box, { paddingBlock: "5", children: [_jsx(Box, { children: _jsx(Link, { href: selectedTab.path, theme: "branded", variant: "primary", size: "md", testId: generateTestId({ testId: TEST_ID, suffix: 'label' }), children: _jsx(TextAlpha, { variant: "subheadingL", children: selectedTab.label }) }) }), _jsxs(Grid, { columns: 12, children: [content.columns.length > 0 && (_jsx(_Fragment, { children: content.columns.map(column => (_jsx(Grid.Cell, { span: 2, children: _jsx(FlyoutColumnLinks, { column: column }) }, `${column.key}-selected-${selectedTab.id}`))) })), (content.teasers.length > 0 || content.teaserColumn) && (_jsx(_Fragment, { children: _jsxs(Grid.Cell, { offset: 9, span: 4, children: [content.teasers.length > 0 && (_jsx(Grid, { columns: getTeaserColumns(teaserType), gap: 4, children: content.teasers.map((teaser, i) => (_jsx(Grid.Cell, { align: "center", contentAlign: "center", span: getTeaserCellColumns(teaserType, i), rowSpan: getTeaserCellRows(teaserType, i), children: teaser.image && (_jsx(TeaserContainer, Object.assign({}, teaser), i)) }, `teaser-${i}-${selectedTab.id}`))) })), content.teaserColumn && (_jsx(Box, { paddingBlockStart: "1.5", children: _jsx(FlyoutColumnLinks, { column: content.teaserColumn }) }))] }) }))] })] }) })), selectedTab && selectedTab.type === 'more' && hiddenItems.length > 0 && (_jsx(Navigation.Content, { children: _jsx(Box, { paddingBlock: "5", children: _jsxs(Stack, { direction: "col", gap: "2", children: [_jsx(Box, { children: _jsx(Link, { href: selectedTab.path, theme: "branded", variant: "primary", size: "md", children: _jsx(TextAlpha, { variant: "subheadingL", children: selectedTab.label }) }) }), _jsx(Stack, { direction: "col", align: "start", children: hiddenItems
                                                                    .filter(elem => elem.type !== 'separator' && elem.type !== 'divider')
                                                                    .map((el, index) => (_jsx(Box, { paddingBlockEnd: "1.5", children: _jsx(Link, { href: el.path, theme: "neutral", variant: "tertiary", size: "md", children: el.label }) }, `link-${el.id}-${index}`))) })] }) }) }))] }) }) }) }) }) })) })] }));
};
const FlyoutColumnLinks = ({ column }) => {
    return (_jsx(Stack, { direction: "col", children: column.items.map((item, index) => {
            var _a;
            return (_jsxs(Stack, { direction: "col", children: [_jsx(Box, { paddingBlockStart: item.afterSpacer ? '8' : '1.5', paddingBlockEnd: "1.5", children: _jsx(Link, { href: item.path, theme: "branded", variant: "primary", size: "md", testId: generateTestId({ testId: TEST_ID, suffix: `${column.key}-block-${index}-title` }), children: item.label }) }), (_a = item.childItems) === null || _a === void 0 ? void 0 : _a.map((child, i) => (_jsx(Box, { paddingBlockStart: child.afterSpacer ? '8' : '1.5', paddingBlockEnd: "1.5", children: _jsx(Link, { href: child.path, theme: "neutral", variant: "tertiary", size: "md", testId: generateTestId({ testId: TEST_ID, suffix: `${column.key}-block-${index}-item-${i}` }), children: child.label }) }, `${column.key}-${child.id}`)))] }, `${column.key}-${item.id}`));
        }) }));
};
export default HeaderContainer;
